import CryptoManager from "./cryptoManager";
import { OtherSession, PrivateChatBootstrapResponse } from "../api/types";
import { createUser } from "../api/userApi";
import { SESSION_DURATION_MS } from "../constants/chat";

function generateRandomName() {
  return `No Name`;
}

function randomizeSecretKey() {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

  return chars
    .split("")
    .sort(() => Math.random() - 0.5)
    .join("");
}

const secretKey = randomizeSecretKey();

export async function initSession() {
  const session = loadSession();
  const startTime = sessionStorage.getItem("sessionStartTime");
  const now = Date.now();

  if (session?.requestToken && startTime) {
    const elapsed = now - Number(startTime);
    if (elapsed < SESSION_DURATION_MS) {
      return { requestToken: session.requestToken, valid: true };
    }
  }

  return await createNewUserSession();
}

async function createNewUserSession() {
  const userName = generateRandomName();
  const publicKeyBase64 = await CryptoManager.ensureKeyPairAndGetPublicBase64();
  const recoverySecret = crypto.randomUUID();

  const recoverySecretHash = await hashSecretKey(recoverySecret);

  const data = await createUser(userName, publicKeyBase64, recoverySecretHash);

  saveSession(data, userName);
  sessionStorage.setItem("sessionStartTime", Date.now().toString());

  window.dispatchEvent(new Event("idless:session-updated"));
  return { valid: true };
}

async function hashSecretKey(secret: string) {
  const encoded = new TextEncoder().encode(secret);
  const hashBuffer = await crypto.subtle.digest("SHA-256", encoded);

  return Array.from(new Uint8Array(hashBuffer))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

export const saveSession = (data: any, userName: string) => {
  const userSession = {
    requestToken: data.requestToken,
    chatNumber: data?.chatNumber ?? "0",
    myUserId: data.senderRef,
    userName,
  };

  sessionStorage.setItem("idlessSession", btoa(JSON.stringify(userSession)));
};

export const loadSession = () => {
  const encoded = sessionStorage.getItem("idlessSession");
  return encoded ? JSON.parse(atob(encoded)) : null;
};

export const clearSession = async () => {
  sessionStorage.clear();

  // optional if your CryptoManager stores anything outside sessionStorage
  // await CryptoManager.clear?.();

  window.dispatchEvent(new Event("idless:session-updated"));
};

export const updateSessionUserName = (userName: string) => {
  const session = loadSession();
  if (!session) return;

  const updatedSession = {
    ...session,
    userName,
  };

  sessionStorage.setItem("idlessSession", btoa(JSON.stringify(updatedSession)));
  window.dispatchEvent(new Event("idless:session-updated"));
};

export const saveOtherSession = (
  data: PrivateChatBootstrapResponse,
  connectionIdentifier?: string,
) => {
  const otherSession: OtherSession = {
    otherUserId: data.id,
    otherUserName: data.resolvedDisplayName ?? data.name ?? "",
    otherUserPubKey: data.publicKey,
    chatId: data.chatId,
    fcmRegistrationId: data.fcmRegistrationId,
    connectionIdentifier,
  };

  sessionStorage.setItem(
    "idlessOtherSession",
    btoa(JSON.stringify(otherSession)),
  );

  sessionStorage.setItem("otherUserId", data.id);
  sessionStorage.setItem("otherUserPubKey", data.publicKey);

  if (connectionIdentifier) {
    sessionStorage.setItem("connectionIdentifier", connectionIdentifier);
  }
};

export const loadOtherSession = (): OtherSession | null => {
  const encoded = sessionStorage.getItem("idlessOtherSession");
  return encoded ? JSON.parse(atob(encoded)) : null;
};

export const expireSessionNow = async () => {
  sessionStorage.setItem("sessionExpired", "true");
  await clearSession();
};